import React, { useState, useEffect } from 'react';
import { Button, Dropdown } from 'antd';
import { ExpandOutlined, DownOutlined } from '@ant-design/icons';

export const ZOOM_OPTIONS = [
  { key: '0', label: '0', zoom: 100 },
  { key: '1', label: '1', zoom: 70 },
  { key: '2', label: '2', zoom: 60 },
  { key: '3', label: '3', zoom: 50 },
] as const;

export type ZoomOptionKey = typeof ZOOM_OPTIONS[number]['key'];

interface ZoomDropdownProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  storageKey: string;
  /** Default option: '0' = 100% (off), '1' = 70%, '2' = 60%, '3' = 50% */
  defaultOption: ZoomOptionKey;
  /** 'dark' = white text on dark nav (default), 'light' = dark text on light nav */
  variant?: 'dark' | 'light';
}

const ZoomDropdown: React.FC<ZoomDropdownProps> = ({
  containerRef,
  storageKey,
  defaultOption,
  variant = 'dark',
}) => {
  const [zoomOption, setZoomOption] = useState<ZoomOptionKey>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === '0' || stored === '1' || stored === '2' || stored === '3') return stored;
      return defaultOption;
    } catch {
      return defaultOption;
    }
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const option = ZOOM_OPTIONS.find((o) => o.key === zoomOption);
    const value = option ? `${option.zoom}%` : '100%';
    const zoomScale = option ? option.zoom / 100 : 1;
    const root = document.documentElement;
    const storageToken = storageKey.replace(/_/g, '-');
    el.style.setProperty('zoom', value);
    root.style.setProperty(`--crm-${storageToken}-zoom-scale`, String(zoomScale));
    root.setAttribute(`data-crm-${storageToken}-zoom-option`, zoomOption);
    window.dispatchEvent(
      new CustomEvent('crm-zoom-change', {
        detail: { storageKey, zoomOption, zoom: option?.zoom ?? 100, scale: zoomScale },
      })
    );
    try {
      localStorage.setItem(storageKey, zoomOption);
    } catch {}
    return () => {
      el.style.setProperty('zoom', '100%');
    };
  }, [zoomOption, storageKey, containerRef]);

  const isDark = variant === 'dark';

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .zoom-dropdown-wrap { display: none !important; }
        }
      `}</style>
      <span className="zoom-dropdown-wrap">
        <Dropdown
          menu={{
            items: ZOOM_OPTIONS.map((opt) => ({
              key: opt.key,
              label: opt.label,
              onClick: () => setZoomOption(opt.key as ZoomOptionKey),
            })),
            selectedKeys: [zoomOption],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<ExpandOutlined style={{ fontSize: 20, color: isDark ? 'white' : '#222' }} />}
            title="Zoom"
            style={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              background: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.04)',
              border: isDark ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.08)',
              borderRadius: 8,
              color: isDark ? 'white' : '#222',
              paddingLeft: 10,
              paddingRight: 10,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600 }}>{zoomOption}</span>
            <DownOutlined style={{ fontSize: 12 }} />
          </Button>
        </Dropdown>
      </span>
    </>
  );
};

export default ZoomDropdown;
